import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { quotesApi, ideasApi } from '@/lib/api/client';
import { CopyOrderButton } from '@/components/CopyOrderButton';
import { TradeSuggestions } from '@/components/TradeSuggestions';
import { SchwabLink } from '@/components/SchwabLink';
import { schwabResearchUrl, SCHWAB_OPTIONS_TICKET_URL } from '@/lib/schwabLinks';
import { formatCurrency } from '@/lib/formatters';
import {
  flattenIdeas, pickHeadlineIdeas, liquidityFlag, ideaToTicket, selectSlice, reviseRequest,
  buildIssueSnapshot,
  type Candidate, type Idea, type TradeSuggestionsResponse,
} from '@/lib/tradeIdeas';
import { ISSUE_REASONS, type IdeaIssueReason } from '@steward/shared';

function IdeaRow({
  label, baseIdea, symbol, response, onReRoll, reRolling, onReport,
}: {
  label: string;
  baseIdea: Idea;
  symbol: string;
  response: TradeSuggestionsResponse;
  onReRoll: (o: { spreadWidth?: number; targetDeltaMin?: number; targetDeltaMax?: number }) => void;
  reRolling: boolean;
  onReport: (idea: Idea) => void;
}) {
  const [open, setOpen] = useState(false);
  const [slice, setSlice] = useState<{ strategy: Idea['strategy']; expiration: string } | null>(null);
  const [width, setWidth] = useState(String(baseIdea.widthPoints));
  const [dMin, setDMin] = useState('');
  const [dMax, setDMax] = useState('');

  const displayed = slice ? selectSlice(response, slice) ?? baseIdea : baseIdea;
  const flag = liquidityFlag(displayed);
  const shortStrikes = displayed.sides.map((s) => `${s.shortStrike}${s.optionType === 'put' ? 'P' : 'C'}`).join(' / ');

  const expirations = Array.from(new Set(response.suggestions.map((s) => s.expiration)));
  const strategies: Idea['strategy'][] = ['putSpread', 'callSpread', 'ironCondor'];

  return (
    <div className="py-2 border-t border-gray-100 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-gray-900">{label}: {displayed.strategyLabel}</div>
          <div className="text-gray-600">
            {displayed.dte} DTE · short {shortStrikes} · {formatCurrency(displayed.credit)} credit · {Number.isFinite(displayed.returnOnRisk) ? (displayed.returnOnRisk * 100).toFixed(1) : '—'}% RoR
          </div>
          {!flag.ok && <div className="text-amber-600">⚠ {flag.reason}</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={() => onReport(displayed)} className="text-xs text-amber-600 underline">⚠ Off</button>
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs text-blue-600 underline">Revise ▾</button>
          <CopyOrderButton order={ideaToTicket(displayed, symbol)} />
        </div>
      </div>

      {open && (
        <div className="mt-2 p-2 rounded bg-gray-50 space-y-2 text-xs">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-gray-500">Strategy:</span>
            {strategies.map((s) => (
              <button key={s} type="button"
                onClick={() => setSlice({ strategy: s, expiration: slice?.expiration ?? displayed.expiration })}
                className={`px-2 py-0.5 rounded border ${displayed.strategy === s ? 'bg-brand-50 border-brand-300' : 'border-gray-200'}`}>
                {s === 'putSpread' ? 'Put' : s === 'callSpread' ? 'Call' : 'IC'}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-gray-500">Exp:</span>
            <select className="border rounded px-1 py-0.5"
              value={slice?.expiration ?? displayed.expiration}
              onChange={(e) => setSlice({ strategy: slice?.strategy ?? displayed.strategy, expiration: e.target.value })}>
              {expirations.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-gray-500">Width:</span>
            <input className="border rounded px-1 py-0.5 w-14" value={width} onChange={(e) => setWidth(e.target.value)} />
            <span className="text-gray-500">Δ min/max:</span>
            <input className="border rounded px-1 py-0.5 w-12" value={dMin} onChange={(e) => setDMin(e.target.value)} />
            <input className="border rounded px-1 py-0.5 w-12" value={dMax} onChange={(e) => setDMax(e.target.value)} />
            <button type="button" disabled={reRolling}
              onClick={() => onReRoll({
                spreadWidth: width ? Number(width) : undefined,
                targetDeltaMin: dMin ? Number(dMin) : undefined,
                targetDeltaMax: dMax ? Number(dMax) : undefined,
              })}
              className="px-2 py-0.5 rounded bg-brand-600 text-white disabled:opacity-50">
              {reRolling ? 'Re-rolling…' : 'Re-roll'}
            </button>
          </div>
          {reRolling && <div className="text-gray-400">Fetching fresh quotes…</div>}
        </div>
      )}
    </div>
  );
}

export function TradeIdeaCard({ candidate, autoLoad }: { candidate: Candidate; autoLoad: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const enabled = autoLoad || expanded;

  const { data, isLoading, error } = useQuery({
    queryKey: ['trade-suggestions', candidate.symbol, 5, 0],
    queryFn: () => quotesApi.getTradeSuggestions({ underlyingSymbol: candidate.symbol, spreadWidth: 5 }),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  const [overrides, setOverrides] = useState<{ spreadWidth?: number; targetDeltaMin?: number; targetDeltaMax?: number } | null>(null);

  const [reportIdea, setReportIdea] = useState<Idea | null>(null);
  const [reasons, setReasons] = useState<IdeaIssueReason[]>([]);
  const [note, setNote] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'saving' | 'error' | 'done'>('idle');

  const REASON_LABELS: Record<IdeaIssueReason, string> = {
    stale_quote: 'stale quote',
    wide_spread: 'wide/crossed spread',
    wrong_credit: 'wrong credit/debit',
    strikes_wrong: 'strikes look wrong',
    other: 'other',
  };

  function openReport(idea: Idea) {
    setReportIdea(idea);
    setReasons([]);
    setNote('');
    setSubmitState('idle');
  }

  async function submitReport() {
    if (!reportIdea || !currentResponse || reasons.length === 0) return;
    setSubmitState('saving');
    try {
      await ideasApi.reportIssue({
        symbol: candidate.symbol,
        strategy: reportIdea.strategy,
        expiration: reportIdea.expiration,
        reasons,
        note: note.trim() || undefined,
        snapshot: buildIssueSnapshot(
          reportIdea, candidate.symbol, currentResponse.underlyingPrice,
          currentResponse.source ?? 'unknown', new Date(),
        ),
      });
      setSubmitState('done');
      setReportIdea(null);
    } catch {
      setSubmitState('error'); // note + selections preserved (state untouched)
    }
  }

  const reRoll = useQuery({
    queryKey: ['trade-suggestions', candidate.symbol, overrides?.spreadWidth ?? 5, overrides?.targetDeltaMin ?? 0, overrides?.targetDeltaMax ?? 0],
    queryFn: () => quotesApi.getTradeSuggestions(
      reviseRequest({ underlyingSymbol: candidate.symbol, spreadWidth: 5 }, overrides ?? {}),
    ),
    enabled: enabled && overrides !== null,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const currentResponse = (reRoll.data ?? data) ?? null;
  const headline = currentResponse ? pickHeadlineIdeas(flattenIdeas(currentResponse), candidate.dominantEngineStrategy) : null;
  const fp = candidate.fingerprint;

  return (
    <Card>
      <CardContent className="py-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold text-gray-900">{candidate.symbol}</span>
              <SchwabLink href={schwabResearchUrl(candidate.symbol)} label={`${candidate.symbol} on Schwab`} ariaLabel={`Research ${candidate.symbol} on Schwab`} />
              <SchwabLink href={SCHWAB_OPTIONS_TICKET_URL} label="Options ticket on Schwab" ariaLabel="Open Schwab options order ticket" />
              {candidate.reasons.map((r) => (
                <span key={r} className="px-2 py-0.5 text-xs rounded-full bg-brand-50 text-brand-700">
                  {r === 'dormant' ? 'Dormant' : 'Top performer'}
                </span>
              ))}
            </div>
            <div className="text-sm text-gray-600">{candidate.rationale}</div>
            {fp && (
              <div className={fp.lowConfidence ? 'text-xs text-gray-400' : 'text-xs text-gray-500'}>
                Your track record here (n={fp.sampleSize}): avg hold ~{Math.round(fp.avgHoldDays)} days, {Math.round(fp.winRate)}% win rate
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-blue-600 hover:text-blue-800 underline flex-shrink-0"
          >
            {expanded ? 'Hide options' : 'More options'}
          </button>
        </div>

        {!enabled && <div className="text-xs text-gray-400">Expand to load ideas.</div>}
        {enabled && isLoading && <div className="flex justify-center py-3"><Spinner /></div>}
        {enabled && error && <div className="text-sm text-red-600">Couldn't generate an idea for {candidate.symbol}.</div>}

        {headline && currentResponse && (
          <div>
            {headline.primary && (
              <IdeaRow label="Your usual" baseIdea={headline.primary} symbol={candidate.symbol}
                response={currentResponse} onReRoll={setOverrides} reRolling={reRoll.isFetching} onReport={openReport} />
            )}
            {headline.secondary && (
              <IdeaRow label="Best premium" baseIdea={headline.secondary} symbol={candidate.symbol}
                response={currentResponse} onReRoll={setOverrides} reRolling={reRoll.isFetching} onReport={openReport} />
            )}
            {!headline.primary && !headline.secondary && (
              <div className="text-sm text-gray-500 border-t border-gray-100 pt-2">No current 10–15Δ idea matched.</div>
            )}
            {!headline.primary && candidate.dominantStrategyType && (
              <div className="text-xs text-gray-400">No current match for your usual {candidate.dominantStrategyType.replace('_', ' ')}.</div>
            )}
            {reRoll.isError && (
              <div className="text-xs text-red-600">
                Re-roll failed — showing the previous idea.{' '}
                <button type="button" onClick={() => void reRoll.refetch()} className="underline">Try again</button>
              </div>
            )}
          </div>
        )}

        {expanded && (
          <div className="pt-2 border-t border-gray-100">
            <TradeSuggestions symbol={candidate.symbol} autoLoad />
          </div>
        )}
      </CardContent>

      <Modal isOpen={reportIdea !== null} onClose={() => setReportIdea(null)} title="Report a problem with this idea" size="md">
        <div className="p-4 space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            {ISSUE_REASONS.map((r) => (
              <button key={r} type="button"
                onClick={() => setReasons((cur) => cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r])}
                className={`px-2 py-1 rounded-full border ${reasons.includes(r) ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-200 text-gray-600'}`}>
                {REASON_LABELS[r]}
              </button>
            ))}
          </div>
          <textarea className="w-full border rounded p-2" rows={3} maxLength={1000}
            placeholder="What's off? (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          {submitState === 'error' && <div className="text-red-600">Couldn't save — your note is kept. Try again.</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setReportIdea(null)} className="px-3 py-1 rounded border">Cancel</button>
            <button type="button" disabled={reasons.length === 0 || submitState === 'saving'} onClick={() => void submitReport()}
              className="px-3 py-1 rounded bg-amber-600 text-white disabled:opacity-50">
              {submitState === 'saving' ? 'Saving…' : 'Submit report'}
            </button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
